import fuzzysort from 'fuzzysort'
import { apps, bundles, utilities, standalone } from '#/constants'
import * as Helpers from '#/helpers'

export const appSearch = (query) => {
  const appArray = Object.keys(apps).map((key) => ({
    key,
    title: apps[key].title,
  }))
  const results =
    query === ''
      ? Object.keys(apps)
      : fuzzysort.go(query, appArray, { key: 'title' }).map((x) => x.obj.key)
  return results || []
}

export const filteredApps = (type, appList, isJira = false, query = '') => {
  switch (type) {
    case 'licensed':
      return Object.keys(appList)
        .filter(
          (k) =>
            !appList[k].bundle &&
            Helpers.appSearch(query).includes(k) &&
            (isJira ? appList[k]?.keys?.jira : appList[k]?.keys?.confluence)
        )
        .sort((a, b) => appList[b].licensed - appList[a].licensed)
    case 'all':
      return Object.keys(appList).filter(
        (k) =>
          k !== 'LITE' &&
          !appList[k].utility &&
          Helpers.appSearch(query).includes(k) &&
          (isJira ? appList[k]?.keys?.jira : appList[k]?.keys?.confluence)
      )
    default:
      return []
  }
}

export const installedStatus = (isJira) => {
  let total = Object.keys({ ...utilities, ...standalone }).filter((k) =>
    isJira ? apps[k]?.keys?.jira : apps[k]?.keys?.confluence
  )
  const installed = total.filter((k) => apps[k].licensed).length
  total = total.length
  return installed === total
    ? `All ${total} installed`
    : `${installed}/${total} installed`
}

export const isLicensed = async (key) => {
  if (!key) return false

  // caching in event that license API endpoint is down
  const cacheKey = `licensed-${key}`
  const cacheObj = localStorage.getItem(cacheKey)
  const cache = cacheObj ? JSON.parse(cacheObj) : null

  // return cached value
  if (cache) {
    if (Date.now() > cache.expires) localStorage.removeItem(cacheKey)
    return cache.value
  }

  return AP.request(`/rest/atlassian-connect/1/addons/${key}`)
    .then((res) => JSON.parse(res.body))
    .then((data) => {
      const isActive = [
        'appsplus-tabs-lite-confluence',
        'appsplus-tabs-lite-jira',
      ].includes(key)
        ? true
        : data.license.active || false

      // cache result if active
      if (isActive) {
        localStorage.setItem(
          cacheKey,
          JSON.stringify({
            value: true,
            expires: Date.now() + 7 * 24 * 60 * 60 * 1000, // 1 week
          })
        )
      }

      return isActive
    })
    .catch(() => {
      console.clear()
      return false
    })
}

export const licensedApps = async (arr, isDemo = null, isJira = false) => {
  // is a demo to show new tab for a bundle
  if (isDemo) {
    const bundled = bundles[isDemo.toUpperCase()]?.included
    Object.keys(apps).forEach((k) => {
      apps[k].licensed =
        isDemo === 'pro' || apps[k]?.utility || bundled?.includes(k)
    })
    return Object.fromEntries(
      Object.entries(apps).filter(([k, v]) => !apps[k].bundle)
    )
  }

  const appsToCheck = [...new Set(arr)]
  const proKey = isJira
    ? 'appsplus-tabs-pro-jira'
    : 'appsplus-tabs-pro-confluence'
  const platform = isJira ? 'jira' : 'confluence'

  // return all licensed if anonymous or Pro
  const anonOrPro = new Promise((resolve) =>
    AP.context.getContext((context) => {
      if (context?.confluence?.macro?.outputType === 'pdf') return resolve(true)
      AP.user.getCurrentUser(async (user) => {
        if (!user.atlassianAccountId || (await isLicensed(proKey))) {
          resolve(true)
        } else {
          resolve(false)
        }
      })
    })
  )

  if (await anonOrPro) {
    Object.keys(apps).forEach((k) => {
      apps[k].licensed = true
    })
    return Object.fromEntries(
      Object.entries(apps).filter(([k, v]) => !apps[k].bundle)
    )
  }

  // set all to false
  Object.keys(apps).forEach((k) => {
    apps[k].licensed = false
  })

  // exclude Pro, Lite and utilities
  const trackOmit = ['LITE', ...Object.keys(utilities)]
  let trackLicensed = trackOmit

  // check bundles
  await Promise.all(
    Object.keys(bundles).map(async (k) => {
      if (
        k !== 'PRO' &&
        !trackLicensed?.includes(k) &&
        (await isLicensed(apps[k].keys?.[platform]))
      ) {
        const included = apps[k]?.included || []
        trackLicensed = [...trackLicensed, ...included, k]
      }
    })
  )

  // check Google and Microsoft cross-compatible apps
  await Promise.all(
    [
      'GOOGLEANALYTICS',
      'GOOGLEDATA',
      'MSONEDRIVE',
      'MSEXCEL',
      'MSPOWERPOINT',
      'MSSTREAM',
      'MSVISIO',
      'MSWORD',
    ].map(async (k) => {
      if (
        !trackLicensed?.includes(k) &&
        (await isLicensed(apps[k].keys?.[platform]))
      ) {
        trackLicensed = [...trackLicensed, k]
      }
    })
  )

  // check remainder
  await Promise.all(
    appsToCheck.map(async (k) => {
      if (
        !apps[k].bundle &&
        !trackLicensed?.includes(k) &&
        (await isLicensed(apps[k].keys?.[platform]))
      ) {
        trackLicensed = [...trackLicensed, k]
      }
    })
  )

  // only Lite version installed
  if (trackLicensed.length === trackOmit.length) {
    trackLicensed = ['NOTE', 'IMAGE', 'WEBSITE', 'PDF', 'CODELIVE']
  }

  // loop apply licensed value
  trackLicensed.forEach((k) => {
    apps[k].licensed = true
  })

  return apps
}

export const googleLicensed = (appKey, appList) => {
  const gApps = ['GOOGLEANALYTICS', 'GOOGLEDATA']
  return (
    gApps?.includes(appKey) &&
    Object.keys(appList)
      .filter((x) => gApps?.includes(x))
      .some((x) => appList[x]?.licensed)
  )
}

export const microsoftLicensed = (appKey, appList) => {
  const msApps = [
    'MSONEDRIVE',
    'MSEXCEL',
    'MSPOWERPOINT',
    'MSSTREAM',
    'MSVISIO',
    'MSWORD',
  ]
  return (
    msApps?.includes(appKey) &&
    Object.keys(appList)
      .filter((x) => msApps?.includes(x))
      .some((x) => appList[x]?.licensed)
  )
}

export const microsoftFollow = (url, setEmbed, setLoading) =>
  fetch('https://api.appsplus.co/follow', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ url }),
  })
    .then((res) => res.json())
    .then((data) => {
      if (data && data.url) {
        setEmbed(`${data.url.replace('.com/redir', '.com/embed')}&em=2`)
        setLoading(false)
      }
    })
    .catch(() => null)
